import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Formik, Form, Field, ErrorMessage, FieldArray } from 'formik';
import * as Yup from 'yup';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './rate.css';

const currencies = [
    "INR", "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN", 
    "BAM", "BBD", "BDT", "BGN", "BHD", "BIF", "BMD", "BND", "BOB", "BRL", 
    "BSD", "BTN", "BWP", "BYN", "BZD", "CAD", "CDF", "CHF", "CLP", "CNY", 
    "COP", "CRC", "CUP", "CVE", "CZK", "DJF", "DKK", "DOP", "DZD", "EGP", 
    "ERN", "ETB", "EUR", "FJD", "FKP", "FOK", "GBP", "GEL", "GGP", "GHS", 
    "GIP", "GMD", "GNF", "GTQ", "GYD", "HKD", "HNL", "HRK", "HTG", "HUF", 
    "IDR", "ILS", "IMP", "IQD", "IRR", "ISK", "JEP", "JMD", "JOD", 
    "JPY", "KES", "KGS", "KHR", "KID", "KMF", "KRW", "KWD", "KYD", "KZT", 
    "LAK", "LBP", "LKR", "LRD", "LSL", "LYD", "MAD", "MDL", "MGA", "MKD", 
    "MMK", "MNT", "MOP", "MRU", "MUR", "MVR", "MWK", "MXN", "MYR", "MZN", 
    "NAD", "NGN", "NIO", "NOK", "NPR", "NZD", "OMR", "PAB", "PEN", "PGK", 
    "PHP", "PKR", "PLN", "PYG", "QAR", "RON", "RSD", "RUB", "RWF", "SAR", 
    "SBD", "SCR", "SDG", "SEK", "SGD", "SHP", "SLL", "SOS", "SRD", "SSP", 
    "STN", "SYP", "SZL", "THB", "TJS", "TMT", "TND", "TOP", "TRY", "TTD", 
    "TVD", "TWD", "TZS", "UAH", "UGX", "USD", "UYU", "UZS", "VES", "VND", 
    "VUV", "WST", "XAF", "XCD", "XDR", "XOF", "XPF", "YER", "ZAR", "ZMW", "ZWL"
];

const getCurrencyLabel = (currencyCode) => {
    try {
        const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: currencyCode, maximumFractionDigits: 0 });
        const parts = formatter.formatToParts(0);
        const symbolPart = parts.find(part => part.type === 'currency');
        return symbolPart ? `${currencyCode} (${symbolPart.value})` : currencyCode;
    } catch(e) {
        return currencyCode;
    }
};

// Validation schema using Yup for Rate Request form
const validationSchema = Yup.object({
    contactNo: Yup.string()
        .matches(/^\d{10}$/, 'Mobile number must be 10 digits')
        .required('Mobile number is required'),
    emailId: Yup.string().email('Invalid email address').required('Email is required'),
    fromAddress: Yup.string().required('Pickup Address is required'),
    fromCity: Yup.string().required('Pickup City is required'),
    fromPincode: Yup.string().required('Pickup Pincode is required'),
    fromState: Yup.string().required('Pickup State is required'),
    fromCountry: Yup.string().required('Pickup Country is required'),
    toAddress: Yup.string().required('Delivery Address is required'),
    toCity: Yup.string().required('Delivery City is required'),
    toPincode: Yup.string().required('Delivery Pincode is required'),
    toState: Yup.string().required('Delivery State is required'),
    toCountry: Yup.string().required('Delivery Country is required'),
    grossWeight: Yup.number().positive('Must be greater than zero').required('Gross Weight is required'),
    noOfBoxes: Yup.number().integer('Must be an integer').positive('Must be at least 1').required('No. of Boxes is required'),
    cargoValue: Yup.number().positive('Must be greater than zero').required('Cargo Value is required'),
    currency: Yup.string().required('Currency is required'),
    dimensions: Yup.array().of(
        Yup.object().shape({
            length: Yup.number().positive('Must be > 0').required('Required'),
            width: Yup.number().positive('Must be > 0').required('Required'),
            height: Yup.number().positive('Must be > 0').required('Required'),
        })
    ).min(1, 'At least one dimension is required'),
    callMailAccepted: Yup.bool().oneOf([true], 'You must accept the terms'),
});

const errorMessageStyle = {
    color: '#e74c3c',
    fontSize: '0.85em',
    marginTop: '6px',
    fontWeight: '600'
};

const RateRequestForm = () => {
    const [otpSent, setOtpSent] = useState(false);
    const [otpInput, setOtpInput] = useState('');
    const [otpError, setOtpError] = useState('');
    const [otpVerified, setOtpVerified] = useState(false);
    const [thankYouMessage, setThankYouMessage] = useState(false);
    const [loading, setLoading] = useState(false);
    const [timer, setTimer] = useState(60); 
    const [timerActive, setTimerActive] = useState(false); 
    const [otpResendEnabled, setOtpResendEnabled] = useState(false);
    const [mobileDisabled, setMobileDisabled] = useState(false); 
    
    // Multi-step Wizard State
    const [currentStep, setCurrentStep] = useState(1);
    const nextStep = () => setCurrentStep(prev => prev < 4 ? prev + 1 : prev);
    const prevStep = () => setCurrentStep(prev => prev > 1 ? prev - 1 : prev);

    const handleSendOtp = async (mobileNo) => {
        if (!mobileNo) return;
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/CheckValidNumberOrNot?mobileNo=${mobileNo}`);
            if (response.data.success) {
                setOtpSent(true);
                setOtpError('');
                setOtpInput('');
                setOtpVerified(false);
                setTimer(60);
                setTimerActive(true);
                setOtpResendEnabled(false);
                setMobileDisabled(true); 
            } else {
                setOtpError('Failed to send OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error sending OTP:', error);
            setOtpError('An error occurred while sending OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    const handleOtpVerify = async (mobileNo) => {
        try {
            setLoading(true);
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/UserDetails/VerifyOtp?mobileNo=${mobileNo}&otp=${otpInput}`);
            if (response.data.success) {
                setOtpVerified(true);
                setOtpError('');
                toast.success('OTP verified successfully!');
                setTimerActive(false);
            } else {
                setOtpError(response.data.message || 'Invalid OTP. Please try again.');
            }
        } catch (error) {
            console.error('Error verifying OTP:', error);
            setOtpError('An error occurred while verifying OTP. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (timerActive && timer > 0) {
            const interval = setInterval(() => {
                setTimer((prev) => prev - 1);
            }, 1000);
            return () => clearInterval(interval);
        } else if (timer === 0) {
            setOtpSent(false);
            setOtpError('OTP expired. Please request a new OTP.');
            setOtpVerified(false);
            setTimerActive(false);
            setOtpResendEnabled(true);
            setMobileDisabled(false); 
        }
    }, [timerActive, timer]);

    const handleSubmit = async (values, { resetForm }) => {
        if (!otpVerified) {
            toast.error('Please verify the OTP before submitting the form.');
            return;
        }

        try {
            setLoading(true);
            const dimensions = values.dimensions;

            const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/RateRequest`, {
                contactNo: values.contactNo,
                emailId: values.emailId,
                fromAddress: values.fromAddress,
                fromCity: values.fromCity,
                fromPincode: values.fromPincode,
                fromState: values.fromState,
                fromCountry: values.fromCountry,
                toAddress: values.toAddress,
                toCity: values.toCity,
                toPincode: values.toPincode,
                toState: values.toState,
                toCountry: values.toCountry,
                grossWeight: values.grossWeight,
                noOfBoxes: values.noOfBoxes,
                cargoValue: values.cargoValue,
                currency: values.currency,
                dimensionsJson: JSON.stringify(dimensions)
            });
            if (response.data.success || response.status === 200) {
                setThankYouMessage(true);
                setOtpSent(false);
                setOtpVerified(false);
                setOtpInput('');
                resetForm();
                setCurrentStep(1);

                setTimeout(() => {
                    setThankYouMessage(false);
                    window.location.reload();
                }, 6000);
            } else {
                toast.error('Failed to submit rate request. Please try again.');
            }
        } catch (error) {
            console.error('Error submitting rate request:', error);
            toast.error('An error occurred while submitting rate request. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="rate-form-container">
            <div className="wizard-steps">
                <div className={`step-item ${currentStep === 1 ? 'active' : currentStep > 1 ? 'completed' : ''}`}>1</div>
                <div className={`step-item ${currentStep === 2 ? 'active' : currentStep > 2 ? 'completed' : ''}`}>2</div>
                <div className={`step-item ${currentStep === 3 ? 'active' : currentStep > 3 ? 'completed' : ''}`}>3</div>
                <div className={`step-item ${currentStep === 4 ? 'active' : currentStep > 4 ? 'completed' : ''}`}>4</div>
            </div>

            <Formik
                initialValues={{
                    contactNo: '', emailId: '', fromAddress: '', fromCity: '', fromPincode: '', fromState: '', fromCountry: '',
                    toAddress: '', toCity: '', toPincode: '', toState: '', toCountry: '',
                    grossWeight: '', noOfBoxes: '', cargoValue: '', currency: 'INR',
                    dimensions: [{ length: '', width: '', height: '' }], callMailAccepted: false,
                }}
                validationSchema={validationSchema}
                validateOnBlur={true}
                validateOnChange={true}
                onSubmit={handleSubmit}
            >
                {({ values, validateForm, setTouched, touched, errors }) => {
                    
                    const handleNextClick = async () => {
                        const formErrors = await validateForm();
                        let stepFields = [];
                        if (currentStep === 1) stepFields = ['contactNo', 'emailId'];
                        if (currentStep === 2) stepFields = ['fromAddress', 'fromCity', 'fromPincode', 'fromState', 'fromCountry'];
                        if (currentStep === 3) stepFields = ['toAddress', 'toCity', 'toPincode', 'toState', 'toCountry'];

                        const currentStepErrors = stepFields.filter(field => formErrors[field]);
                        
                        if (currentStepErrors.length > 0) {
                            const newTouched = currentStepErrors.reduce((acc, field) => ({ ...acc, [field]: true }), {});
                            setTouched({ ...touched, ...newTouched });
                            toast.error('Please fill all required fields correctly before proceeding.');
                        } else if (currentStep === 1 && !otpVerified) {
                            toast.error('Please verify OTP before proceeding.');
                        } else {
                            nextStep();
                        }
                    };

                    return (
                        <Form className="uk-form-stacked">
                            {/* Step 1: Contact Information */}
                            {currentStep === 1 && (
                                <div className="modern-card animate-fade-in">
                                    <h3 className="modern-card-title"><span data-uk-icon="icon: receiver; ratio: 1.2"></span>Contact Information</h3>
                                    
                                    <div className="uk-grid-medium uk-text-left" data-uk-grid>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Mobile No:</label>
                                            <Field type="text" name="contactNo" className="uk-input" disabled={mobileDisabled} placeholder="" />
                                            <ErrorMessage style={errorMessageStyle} name="contactNo" component="div" />
                                            
                                            {!otpVerified && (
                                                <div className="uk-margin-small-top">
                                                    <button type="button" className="modern-btn-primary" onClick={() => handleSendOtp(values.contactNo)} disabled={!values.contactNo || otpSent || loading}>
                                                        {loading ? 'SENDING...' : 'SEND OTP'}
                                                    </button>
                                                </div>
                                            )}
                                        </div>

                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            {otpSent && (
                                                <>
                                                    <label className="uk-form-label uk-text-bold">Enter OTP:</label>
                                                    <Field type="text" name="otp" className="uk-input" value={otpInput} onChange={(e) => setOtpInput(e.target.value)} required disabled={!otpSent || otpVerified} placeholder="" />
                                                    
                                                    {!otpVerified && (
                                                        <div className="uk-margin-small-top">
                                                            <button type="button" className="modern-btn-primary" onClick={() => handleOtpVerify(values.contactNo)} disabled={!otpSent || loading}>
                                                                VERIFY OTP
                                                            </button>
                                                        </div>
                                                    )}
                                                    
                                                    <div className="uk-margin-small-top" style={{ minHeight: '20px' }}>
                                                        {otpVerified && <span className="uk-text-success" style={{ fontWeight: 300, fontSize: '0.9rem' }}>Verified Successfully</span>}
                                                        {otpSent && !otpVerified && (
                                                            <span style={{ color: '#e74c3c', fontWeight: 300, fontSize: '0.9rem' }}>
                                                                {timer > 0 ? `${timer} seconds remaining` : 'OTP expired'}
                                                            </span>
                                                        )}
                                                    </div>
                                                    
                                                    {otpError && <div className="uk-margin-small-top" style={{ color: '#e74c3c', fontSize: '0.85rem', fontWeight: 600 }}>{otpError}</div>}
                                                    {otpResendEnabled && !otpVerified && (
                                                        <div className="uk-margin-small-top">
                                                            <button type="button" className="modern-btn-secondary" style={{ padding: '8px 20px', fontSize: '0.8rem' }} onClick={() => handleSendOtp(values.contactNo)} disabled={loading}>
                                                                Resend OTP
                                                            </button>
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </div>

                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Email:</label>
                                            <Field type="email" name="emailId" className="uk-input" placeholder="" />
                                            <ErrorMessage style={errorMessageStyle} name="emailId" component="div" />
                                        </div>
                                    </div>
                                    <div className="uk-text-right uk-margin-medium-top">
                                        <button type="button" className="modern-btn-primary" onClick={handleNextClick}>NEXT <span data-uk-icon="chevron-right"></span></button>
                                    </div>
                                </div>
                            )}

                            {/* Step 2: Pickup Details */}
                            {currentStep === 2 && (
                                <div className="modern-card animate-fade-in">
                                    <h3 className="modern-card-title"><span data-uk-icon="icon: location; ratio: 1.2"></span>Pickup Details</h3>
                                    <div className="uk-grid-medium uk-text-left" data-uk-grid>
                                        <div className="uk-width-1-1">
                                            <label className="uk-form-label uk-text-bold">Pickup Address</label>
                                            <Field type="text" name="fromAddress" className="uk-input" placeholder="Complete pickup address" />
                                            <ErrorMessage style={errorMessageStyle} name="fromAddress" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">City</label>
                                            <Field type="text" name="fromCity" className="uk-input" placeholder="City" />
                                            <ErrorMessage style={errorMessageStyle} name="fromCity" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Pincode</label>
                                            <Field type="text" name="fromPincode" className="uk-input" placeholder="6-digit Pincode" />
                                            <ErrorMessage style={errorMessageStyle} name="fromPincode" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">State</label>
                                            <Field type="text" name="fromState" className="uk-input" placeholder="State" />
                                            <ErrorMessage style={errorMessageStyle} name="fromState" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Country</label>
                                            <Field type="text" name="fromCountry" className="uk-input" placeholder="Country" />
                                            <ErrorMessage style={errorMessageStyle} name="fromCountry" component="div" />
                                        </div>
                                    </div>
                                    <div className="uk-flex uk-flex-between uk-margin-medium-top">
                                        <button type="button" className="modern-btn-secondary" onClick={prevStep}><span data-uk-icon="chevron-left"></span> Back</button>
                                        <button type="button" className="modern-btn-primary" onClick={handleNextClick}>Next <span data-uk-icon="chevron-right"></span></button>
                                    </div>
                                </div>
                            )}

                            {/* Step 3: Delivery Details */}
                            {currentStep === 3 && (
                                <div className="modern-card animate-fade-in">
                                    <h3 className="modern-card-title"><span data-uk-icon="icon: push; ratio: 1.2"></span>Delivery Details</h3>
                                    <div className="uk-grid-medium uk-text-left" data-uk-grid>
                                        <div className="uk-width-1-1">
                                            <label className="uk-form-label uk-text-bold">Delivery Address</label>
                                            <Field type="text" name="toAddress" className="uk-input" placeholder="Complete delivery address" />
                                            <ErrorMessage style={errorMessageStyle} name="toAddress" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">City</label>
                                            <Field type="text" name="toCity" className="uk-input" placeholder="City" />
                                            <ErrorMessage style={errorMessageStyle} name="toCity" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Pincode</label>
                                            <Field type="text" name="toPincode" className="uk-input" placeholder="6-digit Pincode" />
                                            <ErrorMessage style={errorMessageStyle} name="toPincode" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">State</label>
                                            <Field type="text" name="toState" className="uk-input" placeholder="State" />
                                            <ErrorMessage style={errorMessageStyle} name="toState" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Country</label>
                                            <Field type="text" name="toCountry" className="uk-input" placeholder="Country" />
                                            <ErrorMessage style={errorMessageStyle} name="toCountry" component="div" />
                                        </div>
                                    </div>
                                    <div className="uk-flex uk-flex-between uk-margin-medium-top">
                                        <button type="button" className="modern-btn-secondary" onClick={prevStep}><span data-uk-icon="chevron-left"></span> Back</button>
                                        <button type="button" className="modern-btn-primary" onClick={handleNextClick}>Next <span data-uk-icon="chevron-right"></span></button>
                                    </div>
                                </div>
                            )}

                            {/* Step 4: Cargo Details & Submit */}
                            {currentStep === 4 && (
                                <div className="modern-card animate-fade-in">
                                    <h3 className="modern-card-title"><span data-uk-icon="icon: cart; ratio: 1.2"></span>Cargo Details</h3>
                                    <div className="uk-grid-medium uk-text-left" data-uk-grid>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Gross Weight (Kg)</label>
                                            <Field type="number" name="grossWeight" className="uk-input" step="0.01" />
                                            <ErrorMessage style={errorMessageStyle} name="grossWeight" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Number of Boxes</label>
                                            <Field type="number" name="noOfBoxes" className="uk-input" />
                                            <ErrorMessage style={errorMessageStyle} name="noOfBoxes" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Cargo Value</label>
                                            <Field type="number" name="cargoValue" className="uk-input" step="0.01" />
                                            <ErrorMessage style={errorMessageStyle} name="cargoValue" component="div" />
                                        </div>
                                        <div className="uk-width-1-1 uk-width-1-2@m">
                                            <label className="uk-form-label uk-text-bold">Currency</label>
                                            <Field as="select" name="currency" className="uk-select">
                                                {currencies.map((cur) => (
                                                    <option key={cur} value={cur}>{getCurrencyLabel(cur)}</option>
                                                ))}
                                            </Field>
                                            <ErrorMessage style={errorMessageStyle} name="currency" component="div" />
                                        </div>
                                        
                                        <div className="uk-width-1-1 uk-margin-top">
                                            <h4 className="uk-heading-line uk-text-center"><span>Package Dimensions (in cm)</span></h4>
                                        </div>
                                        <div className="uk-width-1-1">
                                            <FieldArray name="dimensions">
                                                {({ insert, remove, push }) => (
                                                    <div>
                                                        {values.dimensions && values.dimensions.length > 0 && values.dimensions.map((dim, index) => (
                                                            <div className="uk-grid-small uk-flex-bottom uk-margin-small-bottom" data-uk-grid key={index}>
                                                                <div className="uk-width-1-1 uk-width-expand@m">
                                                                    <label className="uk-form-label uk-text-bold">Length</label>
                                                                    <Field type="number" name={`dimensions.${index}.length`} className="uk-input" step="0.01" />
                                                                    <ErrorMessage style={errorMessageStyle} name={`dimensions.${index}.length`} component="div" />
                                                                </div>
                                                                <div className="uk-width-1-1 uk-width-expand@m">
                                                                    <label className="uk-form-label uk-text-bold">Width</label>
                                                                    <Field type="number" name={`dimensions.${index}.width`} className="uk-input" step="0.01" />
                                                                    <ErrorMessage style={errorMessageStyle} name={`dimensions.${index}.width`} component="div" />
                                                                </div>
                                                                <div className="uk-width-1-1 uk-width-expand@m">
                                                                    <label className="uk-form-label uk-text-bold">Height</label>
                                                                    <Field type="number" name={`dimensions.${index}.height`} className="uk-input" step="0.01" />
                                                                    <ErrorMessage style={errorMessageStyle} name={`dimensions.${index}.height`} component="div" />
                                                                </div>
                                                                <div className="uk-width-1-1 uk-width-auto@m">
                                                                    {values.dimensions.length > 1 ? (
                                                                        <button type="button" className="modern-btn-secondary" style={{ padding: '8px 15px', color: '#e74c3c', width: '100%' }} onClick={() => remove(index)}>
                                                                            <span data-uk-icon="icon: trash"></span> REMOVE
                                                                        </button>
                                                                    ) : (
                                                                        <div style={{ width: '47px' }}></div>
                                                                    )}
                                                                </div>
                                                            </div>
                                                        ))}
                                                        <div className="uk-margin-small-top uk-text-left">
                                                            <button type="button" className="modern-btn-secondary" style={{ padding: '8px 15px' }} onClick={() => push({ length: '', width: '', height: '' })}>
                                                                + Add
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </FieldArray>
                                        </div>
                                    </div>
                                    
                                    <div className="uk-width-1-1 uk-margin-medium-top">
                                        <div className="uk-text-left" style={{ display: 'flex', alignItems: 'flex-start', cursor: 'pointer' }}>
                                            <Field type="checkbox" name="callMailAccepted" className="uk-checkbox" style={{ marginTop: '4px', flexShrink: 0 }} />
                                            <label className="uk-form-label uk-text-bold" style={{ margin: 0, paddingLeft: '8px', cursor: 'pointer' }}>
                                            I agree and allow STRATOS AIRMAIL representative to call or e-mail regarding the enquiry.
                                            </label>
                                        </div>
                                        <div className="uk-text-left">
                                            <ErrorMessage style={errorMessageStyle} name="callMailAccepted" component="div" className="error-message" />
                                        </div>
                                    </div>

                                    <div className="uk-flex uk-flex-between uk-flex-middle uk-margin-medium-top">
                                        <button type="button" className="modern-btn-secondary" onClick={prevStep}><span data-uk-icon="chevron-left"></span> Back</button>
                                        <button type="submit" className="modern-btn-primary" disabled={!otpVerified || loading}>
                                            {loading ? 'Submitting...' : 'Submit Request'} <span data-uk-icon="check"></span>
                                        </button>
                                    </div>
                                </div>
                            )}
                            

                        </Form>
                    );
                }}
            </Formik>

            {thankYouMessage && (
                <div className="uk-alert-success">
                    <p>Thank you for submitting your rate request! We will get back to you soon.</p>
                </div>
            )}

            <ToastContainer position="top-center" autoClose={5000} hideProgressBar={false} />
        </div>
    );
};

export default RateRequestForm;
